<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useMessageContext } from '../provider.js';
import Icon from 'next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { MESSAGE_VARIANTS } from '../constants';

const emit = defineEmits(['error']);
const { variant, content, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const hasImgStoryError = ref(false);
const hasVideoStoryError = ref(false);
const blobUrl = ref(null);
const isLoading = ref(true);

const formattedContent = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return content.value;
  }

  return new MessageFormatter(content.value).formattedMessage;
});

// Fetch media with headers to bypass ngrok warning page in development
const loadMedia = async () => {
  const url = attachment.value?.dataUrl;
  if (!url) {
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    const response = await fetch(url, {
      headers: { 'ngrok-skip-browser-warning': 'true' },
    });

    if (!response.ok) {
      throw new Error('Failed to load media');
    }

    const blob = await response.blob();
    blobUrl.value = URL.createObjectURL(blob);
  } catch {
    hasImgStoryError.value = true;
    hasVideoStoryError.value = true;
    emit('error');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadMedia);
watch(() => attachment.value?.dataUrl, loadMedia);

const onImageLoadError = () => {
  hasImgStoryError.value = true;
  emit('error');
};

const onVideoLoadError = () => {
  hasVideoStoryError.value = true;
  emit('error');
};
</script>

<template>
  <BaseBubble class="p-3 overflow-hidden" data-bubble-name="ig-story">
    <div v-if="content" v-dompurify-html="formattedContent" class="mb-2" />
    <div
      v-if="isLoading"
      class="flex items-center justify-center w-48 h-48 bg-n-slate-3 rounded-lg"
    >
      <Icon icon="i-lucide-loader-2" class="text-n-slate-11 animate-spin" />
    </div>
    <img
      v-else-if="blobUrl && !hasImgStoryError"
      class="rounded-lg max-w-80 skip-context-menu"
      :src="blobUrl"
      @error="onImageLoadError"
    />
    <video
      v-else-if="blobUrl && !hasVideoStoryError"
      class="rounded-lg max-w-80 skip-context-menu"
      controls
      :src="blobUrl"
      @error="onVideoLoadError"
    />
    <div
      v-else
      class="flex items-center gap-1 px-5 py-4 text-center rounded-lg bg-n-alpha-1"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.FILE_BUBBLE.INSTAGRAM_STORY_UNAVAILABLE') }}
      </p>
    </div>
  </BaseBubble>
</template>
